// Response-local timing stays outside API DTOs and is released with each snapshot.
// A later request (or another backend) must never change an older snapshot's clock.
const clocks = new WeakMap<object, { serverTime: number | null; receivedAt: number }>();

export function rememberResponseTime(value: unknown, headers: Headers, startedAt: number): void {
  if (!value || typeof value !== "object") return;
  const receivedAt = performance.now();
  const date = Date.parse(headers.get("Date") ?? "");
  const ageHeader = headers.get("Age");
  const age = ageHeader === null ? 0 : /^\d+$/.test(ageHeader) ? Number(ageHeader) * 1_000 : NaN;
  // HTTP Date has second precision. Charge the whole request duration and Age
  // conservatively so transport/caching never extends the snapshot's lifetime.
  const serverTime = date + 1_000 + age + Math.max(0, receivedAt - startedAt);
  clocks.set(value, { serverTime: Number.isFinite(serverTime) ? serverTime : null, receivedAt });
}

export function responseNow(value: object | null): number | null {
  const clock = value ? clocks.get(value) : undefined;
  // Locally constructed legacy/demo fixtures have no HTTP timing metadata.
  if (!clock) return Date.now();
  if (clock.serverTime === null) return null;
  return clock.serverTime + Math.max(0, performance.now() - clock.receivedAt);
}

/** Keep a derived in-memory review on the original response's clock. */
export function inheritResponseTime(source: object, target: object): void {
  const clock = clocks.get(source);
  if (clock) clocks.set(target, clock);
}

export function responseSecondsRemaining(value: object | null, deadline: number): number {
  const now = responseNow(value);
  return now === null ? 0 : Math.max(0, deadline - Math.floor(now / 1_000));
}
